<template>
  <v-dialog v-model="internalShow" max-width="760" scrollable>
    <v-card class="qb-modal">
      <!-- ── Header ─────────────────────────────────────────────── -->
      <v-card-title class="qb-modal__header">
        <div class="qb-modal__title-row">
          <span class="qb-modal__logo">
            <QuickbooksIcon :connected="isConnected" size="28" />
          </span>
          <div>
            <div class="qb-modal__school-name">{{ school.school_name }}</div>
            <div class="qb-modal__subtitle">QuickBooks</div>
          </div>
          <v-spacer />
          <v-btn icon variant="plain" @click="internalShow = false">
            <v-icon color="gray600">mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="qb-modal__body">
        <!-- ══ CONNECTED VIEW ═══════════════════════════════════════ -->
        <template v-if="isConnected">
          <div class="qb-modal__connected-header">
            <div class="qb-modal__connected-info">
              <v-icon color="green" size="18">mdi-link-variant</v-icon>
              <span class="qb-modal__connected-label">
                QB Customer ID: <strong>{{ school.status?.ref_id }}</strong>
              </span>
            </div>
            <v-btn
              size="small"
              variant="outlined"
              color="red"
              :loading="state.unlinking"
              @click="confirmUnlink"
            >
              <v-icon start size="16">mdi-link-variant-off</v-icon>
              Disconnect
            </v-btn>
          </div>

          <!-- Invoices list -->
          <div v-if="state.loadingInvoices" class="qb-modal__loading">
            <v-progress-circular indeterminate color="green" size="28" />
            <span>Loading invoices…</span>
          </div>

          <div v-else-if="state.invoiceError" class="qb-modal__error">
            <v-icon color="red" size="20">mdi-alert-circle-outline</v-icon>
            {{ state.invoiceError }}
          </div>

          <div v-else-if="!state.invoices.length" class="qb-modal__empty">
            No invoices found for this customer.
          </div>

          <v-table v-else density="compact" class="qb-modal__table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Due Date</th>
                <th class="text-right">Total</th>
                <th class="text-right">Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inv in state.invoices" :key="inv.Id">
                <td>
                  <a
                    href="#"
                    class="qb-modal__invoice-link"
                    :class="{ 'qb-modal__invoice-link--loading': state.downloadingPdf === inv.Id }"
                    @click.prevent="downloadPdf(inv.Id, inv.DocNumber)"
                    :title="`Download invoice #${inv.DocNumber} as PDF`"
                  >
                    <v-icon v-if="state.downloadingPdf === inv.Id" size="12" class="qb-modal__pdf-spinner">mdi-loading</v-icon>
                    <v-icon v-else size="12" color="red">mdi-file-pdf-box</v-icon>
                    {{ inv.DocNumber }}
                  </a>
                </td>
                <td>{{ inv.TxnDate }}</td>
                <td>{{ inv.DueDate ?? '—' }}</td>
                <td class="text-right">${{ formatMoney(inv.TotalAmt) }}</td>
                <td class="text-right">${{ formatMoney(inv.Balance) }}</td>
                <td>
                  <v-chip
                    size="x-small"
                    :color="inv.Balance == 0 ? 'green' : 'orange'"
                    variant="tonal"
                  >
                    {{ inv.Balance == 0 ? 'Paid' : 'Outstanding' }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-table>
        </template>

        <!-- ══ CONNECT VIEW ════════════════════════════════════════ -->
        <template v-else>
          <div class="qb-modal__connect-intro">
            <v-icon color="gray400" size="20">mdi-information-outline</v-icon>
            <span>This school is not linked to a QuickBooks customer. Select an existing customer or create a new one.</span>
          </div>

          <!-- Tab switcher -->
          <v-tabs v-model="state.connectTab" color="green" density="compact" class="mb-4">
            <v-tab value="select">Select Existing Customer</v-tab>
            <v-tab value="create">Create New Customer</v-tab>
          </v-tabs>

          <!-- ── Select existing ───────────────────────────── -->
          <v-window v-model="state.connectTab">
            <v-window-item value="select">
              <v-text-field
                v-model="state.customerSearch"
                label="Search customers by name or email"
                prepend-inner-icon="mdi-magnify"
                clearable
                density="compact"
                variant="outlined"
                color="green"
                :loading="state.searchingCustomers"
                @update:model-value="debouncedSearch"
                class="mb-3"
              />

              <!-- Direct QB ID link — for when the customer ID is already known -->
              <div class="qb-modal__direct-link">
                <v-text-field
                  v-model="state.directQbId"
                  label="Or enter QB Customer ID directly"
                  density="compact"
                  variant="outlined"
                  color="green"
                  placeholder="e.g. 2778"
                  hide-details
                  style="flex: 1"
                />
                <v-btn
                  color="green"
                  variant="tonal"
                  :disabled="!state.directQbId.trim()"
                  :loading="state.linking"
                  @click="linkByDirectId"
                >
                  Link
                </v-btn>
              </div>

              <div v-if="state.customerSearchError" class="qb-modal__error mb-2">
                {{ state.customerSearchError }}
              </div>

              <!-- Suggestions / search results -->
              <div v-if="state.loadingInitialSuggestions" class="qb-modal__loading">
                <v-progress-circular indeterminate color="green" size="22" />
                <span>Finding matching customers…</span>
              </div>

              <div
                v-else-if="!state.customerResults.length"
                class="qb-modal__empty"
              >
                {{ state.customerSearch ? 'No customers found.' : 'No matching customers found. Try searching by name or email.' }}
              </div>

              <v-radio-group
                v-else-if="state.customerResults.length"
                v-model="state.selectedCustomerId"
                class="qb-modal__radio-group"
              >
                <v-radio
                  v-for="c in state.customerResults"
                  :key="c.id"
                  :value="c.id"
                  class="qb-modal__radio-item"
                >
                  <template #label>
                    <div class="qb-modal__customer-label">
                      <span class="qb-modal__customer-name">{{ c.display_name }}</span>
                      <span v-if="c.email" class="qb-modal__customer-meta">{{ c.email }}</span>
                      <span v-if="c.phone" class="qb-modal__customer-meta">{{ c.phone }}</span>
                      <v-chip size="x-small" variant="tonal" color="gray400">ID: {{ c.id }}</v-chip>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>

              <v-btn
                color="green"
                :disabled="!state.selectedCustomerId"
                :loading="state.linking"
                block
                @click="linkSelected"
                class="mt-3"
              >
                <v-icon start>mdi-link-variant</v-icon>
                Link Selected Customer
              </v-btn>
            </v-window-item>

            <!-- ── Create new ──────────────────────────────────── -->
            <v-window-item value="create">
              <div class="qb-modal__create-form">
                <v-text-field
                  v-model="state.newCustomer.display_name"
                  label="Display Name *"
                  density="compact"
                  variant="outlined"
                  color="green"
                  class="mb-2"
                />
                <v-text-field
                  v-model="state.newCustomer.company_name"
                  label="Company Name"
                  density="compact"
                  variant="outlined"
                  color="green"
                  class="mb-2"
                />
                <v-text-field
                  v-model="state.newCustomer.email"
                  label="Email"
                  density="compact"
                  variant="outlined"
                  color="green"
                  class="mb-2"
                />
                <v-text-field
                  v-model="state.newCustomer.phone"
                  label="Phone"
                  density="compact"
                  variant="outlined"
                  color="green"
                  class="mb-2"
                />
                <v-text-field
                  v-model="state.newCustomer.address"
                  label="Address"
                  density="compact"
                  variant="outlined"
                  color="green"
                  class="mb-2"
                />
                <div class="d-flex gap-2">
                  <v-text-field
                    v-model="state.newCustomer.city"
                    label="City"
                    density="compact"
                    variant="outlined"
                    color="green"
                  />
                  <v-text-field
                    v-model="state.newCustomer.state"
                    label="State"
                    density="compact"
                    variant="outlined"
                    color="green"
                    style="max-width: 90px"
                  />
                  <v-text-field
                    v-model="state.newCustomer.zip"
                    label="Zip"
                    density="compact"
                    variant="outlined"
                    color="green"
                    style="max-width: 110px"
                  />
                </div>
                <v-btn
                  color="green"
                  :disabled="!state.newCustomer.display_name"
                  :loading="state.creatingCustomer"
                  block
                  @click="createCustomer"
                  class="mt-2"
                >
                  <v-icon start>mdi-plus</v-icon>
                  Create &amp; Link Customer
                </v-btn>
              </div>
            </v-window-item>
          </v-window>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Unlink confirmation dialog -->
  <v-dialog v-model="state.confirmUnlinkDialog" max-width="400">
    <v-card>
      <v-card-title>Disconnect from QuickBooks?</v-card-title>
      <v-card-text>
        This will remove the QuickBooks customer link for
        <strong>{{ school.school_name }}</strong>. You can reconnect at any time.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="plain" @click="state.confirmUnlinkDialog = false">Cancel</v-btn>
        <v-btn color="red" :loading="state.unlinking" @click="doUnlink">Disconnect</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import type { GetSchoolDetails } from "@/api_types/school";
import { useSchools } from "@/stores/support/schools";
import { useAlertsStore } from "@/stores/alerts";
import QuickbooksIcon from "@/components/support/QuickbooksIcon.vue";
import { debounce } from "lodash";
import authHeader from "@/services/auth-header";
import FetchService from "@/services/fetch.service";

const props = defineProps<{
  show: boolean;
  school: GetSchoolDetails;
}>();

const emit = defineEmits<{
  (e: "update:show", val: boolean): void;
}>();

const schoolStore = useSchools();
const alerts = useAlertsStore();

const internalShow = computed({
  get: () => props.show,
  set: (v) => emit("update:show", v),
});

const isConnected = computed(() => !!props.school?.status?.ref_id);

interface QbCustomer {
  id: string;
  display_name: string;
  company_name: string;
  email: string;
  phone: string;
}

interface QbInvoice {
  Id: string;
  DocNumber: string;
  TxnDate: string;
  DueDate?: string;
  TotalAmt: number;
  Balance: number;
}

const state = reactive({
  // Invoices (connected view)
  loadingInvoices: false,
  invoices: [] as QbInvoice[],
  invoiceError: "",
  downloadingPdf: "" as string,

  // Connect — select tab
  connectTab: "select" as "select" | "create",
  customerSearch: "",
  searchingCustomers: false,
  customerResults: [] as QbCustomer[],
  customerSearchError: "",
  selectedCustomerId: "" as string,
  loadingInitialSuggestions: false,
  linking: false,
  directQbId: "",

  // Connect — create tab
  newCustomer: {
    display_name: "",
    company_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  },
  creatingCustomer: false,

  // Unlink
  unlinking: false,
  confirmUnlinkDialog: false,
});

// ── Watchers ──────────────────────────────────────────────────────────

watch(
  () => props.show,
  async (open) => {
    if (!open) return;
    resetState();

    if (isConnected.value) {
      await loadInvoices();
    } else {
      await loadInitialSuggestions();
    }
  }
);

// ── Helpers ───────────────────────────────────────────────────────────

function resetState() {
  state.invoices = [];
  state.invoiceError = "";
  state.customerSearch = "";
  state.customerResults = [];
  state.selectedCustomerId = "";
  state.customerSearchError = "";
  state.directQbId = "";
  state.connectTab = "select";
  Object.assign(state.newCustomer, {
    display_name: props.school?.school_name ?? "",
    company_name: "",
    email: props.school?.billing_email ?? props.school?.school_contact_email ?? "",
    phone: props.school?.billing_phone ?? props.school?.school_contact_phone ?? "",
    address: props.school?.billing_address1 ?? props.school?.school_contact_address1 ?? "",
    city: props.school?.billing_city ?? props.school?.school_contact_city ?? "",
    state: props.school?.billing_state ?? props.school?.school_contact_state ?? "",
    zip: props.school?.billing_zip ?? props.school?.school_contact_zip ?? "",
  });
}

async function loadInvoices() {
  state.loadingInvoices = true;
  state.invoiceError = "";
  try {
    const res = await schoolStore.getQbInvoices(props.school.school_id);
    state.invoices = res?.invoices ?? [];
    // Sort newest first
    state.invoices.sort((a, b) => (b.TxnDate > a.TxnDate ? 1 : -1));
  } catch {
    state.invoiceError = "Failed to load invoices. Please try again.";
  } finally {
    state.loadingInvoices = false;
  }
}

async function loadInitialSuggestions() {
  state.loadingInitialSuggestions = true;
  state.customerSearchError = "";
  try {
    const res = await schoolStore.searchQbCustomers("", props.school.school_id);
    state.customerResults = res?.customers ?? [];
    if (res?.error) {
      state.customerSearchError = res.error;
    }
  } catch {
    state.customerSearchError = "Could not load customer suggestions. You can still search manually.";
  } finally {
    state.loadingInitialSuggestions = false;
  }
}

const debouncedSearch = debounce(async (query: string) => {
  if (!query || query.length < 2) {
    if (!query) await loadInitialSuggestions();
    return;
  }
  state.searchingCustomers = true;
  state.customerSearchError = "";
  try {
    const res = await schoolStore.searchQbCustomers(query, props.school.school_id);
    state.customerResults = res?.customers ?? [];
  } catch {
    state.customerSearchError = "Search failed. Please try again.";
  } finally {
    state.searchingCustomers = false;
  }
}, 400);

// ── Actions ───────────────────────────────────────────────────────────

async function linkSelected() {
  if (!state.selectedCustomerId) return;
  state.linking = true;
  try {
    const res = await schoolStore.linkQbCustomer(props.school.school_id, state.selectedCustomerId);
    if (res?.status) {
      alerts.addAlert("success", "School linked to QuickBooks customer.", 5);
      await loadInvoices();
    } else {
      alerts.addAlert("error", res?.error ?? "Failed to link customer.", 7);
    }
  } finally {
    state.linking = false;
  }
}

async function createCustomer() {
  if (!state.newCustomer.display_name) return;
  state.creatingCustomer = true;
  try {
    const res = await schoolStore.createQbCustomer({
      school_id: props.school.school_id,
      ...state.newCustomer,
    });
    if (res?.status) {
      alerts.addAlert("success", `QuickBooks customer created and linked.`, 5);
      await loadInvoices();
    } else {
      alerts.addAlert("error", res?.error ?? "Failed to create customer.", 7);
    }
  } finally {
    state.creatingCustomer = false;
  }
}

function confirmUnlink() {
  state.confirmUnlinkDialog = true;
}

async function doUnlink() {
  state.unlinking = true;
  try {
    const res = await schoolStore.unlinkQbCustomer(props.school.school_id);
    if (res?.status) {
      alerts.addAlert("success", "QuickBooks customer disconnected.", 5);
      state.confirmUnlinkDialog = false;
      state.invoices = [];
      // Reset to connect view — load suggestions
      await loadInitialSuggestions();
    } else {
      alerts.addAlert("error", res?.error ?? "Failed to disconnect.", 7);
    }
  } finally {
    state.unlinking = false;
  }
}

function formatMoney(val: number | string): string {
  return Number(val ?? 0).toFixed(2);
}

async function downloadPdf(invoiceId: string, docNumber: string) {
  if (state.downloadingPdf) return;
  state.downloadingPdf = invoiceId;
  try {
    const url = FetchService.getUrl(`/quickbooks/invoices/${invoiceId}/pdf`);
    const res = await fetch(url, { headers: authHeader(null) });
    if (!res.ok) {
      alerts.addAlert("error", `Failed to download invoice #${docNumber}`, 5);
      return;
    }
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `invoice-${docNumber}.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch {
    alerts.addAlert("error", `Failed to download invoice #${docNumber}`, 5);
  } finally {
    state.downloadingPdf = "";
  }
}
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.qb-modal {
  &__header {
    padding: 16px 20px 12px;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__logo {
    display: flex;
    align-items: center;
  }

  &__school-name {
    font-weight: 600;
    font-size: 1rem;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: #666;
  }

  &__body {
    padding: 20px;
    min-height: 300px;
  }

  &__connected-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 10px 14px;
    background: #f0fdf4;
    border-radius: 8px;
    border: 1px solid #bbf7d0;
  }

  &__connected-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
  }

  &__connected-label {
    color: #333;
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 24px;
    color: #666;
    font-size: 0.875rem;
    justify-content: center;
  }

  &__error {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #c00;
    font-size: 0.875rem;
    padding: 8px 0;
  }

  &__empty {
    color: #888;
    font-size: 0.875rem;
    padding: 24px 0;
    text-align: center;
  }

  &__table {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    font-size: 0.8125rem;
  }

  &__connect-intro {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.875rem;
    color: #555;
    margin-bottom: 16px;
    padding: 10px 14px;
    background: #f8f9fa;
    border-radius: 8px;
  }

  &__radio-group {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 4px 8px;
  }

  &__radio-item {
    border-bottom: 1px solid #f3f4f6;
    padding: 6px 0;

    &:last-child {
      border-bottom: none;
    }
  }

  &__customer-label {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding-left: 4px;
  }

  &__customer-name {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__customer-meta {
    font-size: 0.75rem;
    color: #777;
  }

  &__invoice-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #1565c0;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }

    &--loading {
      opacity: 0.6;
      pointer-events: none;

      .qb-modal__pdf-spinner {
        animation: spin 1s linear infinite;
      }
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}
</style>
